import { useContext } from 'react';
import ServicesContext from '../../global/services/ServicesContext';
import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { mutateDocumentsKeys } from './mutateKeyFactory';
import { useSearchContext } from '../search/SearchContext';
import { searchKeys } from '../search/queryKeyFactory';
import { DeleteDocumentsRequest } from '../../Model/requests/DeleteDocumentsRequest';
import { DeleteDocumentsResponse } from '../../Model/responseDtos/DeleteDocumentsResponse';
import { GearSearchQueryResult } from '../../Model/GearSearchQueryResult';
import { GearDocumentWithAllowedActions } from '../../Model/GearDocumentWithAllowedActions';
import { DOC_ID_FIELD } from '../../config/consts';
import { v4 as uuidv4 } from 'uuid';
import { useNotificationContext } from '../../global/hooks/useNotificationContext';
import { useGlobalizationContext } from '../globalization/GlobalizationContext';
import { useTranslation } from '../globalization/useTranslation';

export function useDeleteDocuments(): UseMutationResult<
  DeleteDocumentsResponse,
  unknown,
  { dto: DeleteDocumentsRequest }
> {
  const servicesCtx = useContext(ServicesContext);
  const queryClient = useQueryClient();
  const { queryRequest } = useSearchContext();
  const { addCenterNotification } = useNotificationContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);

  const deleteDocuments = (data: { dto: DeleteDocumentsRequest }): Promise<DeleteDocumentsResponse> => {
    if (servicesCtx?.documentsService && data.dto) {
      return servicesCtx.documentsService?.deleteDocuments(data.dto);
    }
    throw new Error('Services context not initialized');
  };

  return useMutation({
    mutationKey: mutateDocumentsKeys.deleteDocuments,
    mutationFn: (data: { dto: DeleteDocumentsRequest }) => deleteDocuments(data),
    onMutate: async data => {
      if (!queryRequest) {
        return { previousData: undefined };
      }

      await queryClient.cancelQueries({ queryKey: searchKeys.filteredSearch(queryRequest) });

      const previousData = queryClient.getQueryData<GearSearchQueryResult>(searchKeys.filteredSearch(queryRequest));

      queryClient.setQueryData<GearSearchQueryResult>(searchKeys.filteredSearch(queryRequest), old => {
        if (!old?.documentsWithPerms) {
          return old;
        }

        return {
          ...old,
          documentsWithPerms: old.documentsWithPerms.filter(
            (item: GearDocumentWithAllowedActions) =>
              !data.dto.documents.some(doc => doc.documentId === item.document[DOC_ID_FIELD])
          )
        };
      });

      return { previousData };
    },
    onSuccess: data => {
      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentSuccessTitle'),
        message: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentSuccessMessage', {
          deleted: data.deleted.length,
          total: data.deleted.length + data.skipped.length
        }),
        timestamp: new Date(),
        type: 'success'
      });
    },
    onError: (err, newData, context) => {
      if (queryRequest && context?.previousData) {
        queryClient.setQueryData(searchKeys.filteredSearch(queryRequest), context.previousData);
      }

      addCenterNotification({
        id: uuidv4(),
        title: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentFailedTitle'),
        message: translate('custom.notifications.notificationCenter.deleteDocument.deleteDocumentFailedMessage'),
        timestamp: new Date(),
        type: 'error'
      });
    }
  });
}
